import { Directive, ElementRef, Renderer2, OnInit, OnDestroy } from '@angular/core';

@Directive({
  selector: '[appColumnResize]',
  standalone: false
})
export class ColumnResizeDirective implements OnInit, OnDestroy {
  private resizeHandle!: HTMLElement;
  private isResizing = false;
  private hasDragged = false;
  private startX = 0;
  private startWidth = 0;
  private minWidth = 80;
  private boundMouseDown!: (event: MouseEvent) => void;
  private boundMouseMove!: (event: MouseEvent) => void;
  private boundMouseUp!: (event: MouseEvent) => void;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.boundMouseDown = this.onMouseDown.bind(this);
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);
    this.createResizeHandle();
    this.restoreWidth();
  }

  private getStorageKey(): string {
    const pathname = window.location.pathname;
    const th = this.el.nativeElement as HTMLElement;
    const parent = th.parentElement;
    const index = parent ? Array.from(parent.children).indexOf(th) : -1;
    return `col-resize:${pathname}:${index}`;
  }

  private restoreWidth(): void {
    try {
      const saved = localStorage.getItem(this.getStorageKey());
      if (saved) {
        const width = Number(saved);
        if (isFinite(width) && width >= this.minWidth) {
          this.renderer.setStyle(this.el.nativeElement, 'width', `${width}px`);
          this.renderer.setStyle(this.el.nativeElement, 'min-width', `${width}px`);
          this.renderer.setStyle(this.el.nativeElement, 'max-width', `${width}px`);
        }
      }
    } catch {
      // localStorage unavailable (e.g. Safari private mode) — skip restore
    }
  }

  private saveWidth(width: number): void {
    try {
      localStorage.setItem(this.getStorageKey(), String(width));
    } catch {
      // localStorage unavailable or full — skip save
    }
  }

  ngOnDestroy(): void {
    if (this.isResizing) {
      this.renderer.removeClass(document.body, 'resizing-column');
      this.renderer.setStyle(document.body, 'cursor', 'default');
      this.renderer.setStyle(document.body, 'user-select', 'auto');
    }
    if (this.resizeHandle) {
      this.resizeHandle.removeEventListener('mousedown', this.boundMouseDown);
      this.resizeHandle.remove();
    }
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }

  private createResizeHandle(): void {
    this.resizeHandle = this.renderer.createElement('div');
    this.renderer.addClass(this.resizeHandle, 'resize-handle');
    this.renderer.setStyle(this.resizeHandle, 'position', 'absolute');
    this.renderer.setStyle(this.resizeHandle, 'top', '0');
    this.renderer.setStyle(this.resizeHandle, 'right', '0');
    this.renderer.setStyle(this.resizeHandle, 'width', '8px');
    this.renderer.setStyle(this.resizeHandle, 'height', '100%');
    this.renderer.setStyle(this.resizeHandle, 'cursor', 'col-resize');
    this.renderer.setStyle(this.resizeHandle, 'z-index', '10');
    this.renderer.setStyle(this.resizeHandle, 'user-select', 'none');

    this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    this.renderer.setStyle(this.el.nativeElement, 'user-select', 'none');
    this.renderer.appendChild(this.el.nativeElement, this.resizeHandle);

    this.resizeHandle.addEventListener('mousedown', this.boundMouseDown);

    this.resizeHandle.addEventListener('mouseenter', () => {
      if (!this.isResizing) {
        this.renderer.setStyle(this.resizeHandle, 'background-color', 'rgba(0, 136, 255, 0.2)');
      }
    });

    this.resizeHandle.addEventListener('mouseleave', () => {
      if (!this.isResizing) {
        this.renderer.setStyle(this.resizeHandle, 'background-color', 'transparent');
      }
    });
  }

  private onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.hasDragged = false;
    this.startX = event.pageX;
    this.startWidth = this.el.nativeElement.offsetWidth;

    this.renderer.setStyle(this.resizeHandle, 'background-color', 'rgba(0, 136, 255, 0.5)');
    this.renderer.addClass(document.body, 'resizing-column');
    this.renderer.setStyle(document.body, 'cursor', 'col-resize');
    this.renderer.setStyle(document.body, 'user-select', 'none');

    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isResizing) return;

    event.preventDefault();
    event.stopPropagation();

    this.hasDragged = true;
    const diff = event.pageX - this.startX;
    const newWidth = Math.max(this.minWidth, this.startWidth + diff);

    this.renderer.setStyle(this.el.nativeElement, 'width', `${newWidth}px`);
    this.renderer.setStyle(this.el.nativeElement, 'min-width', `${newWidth}px`);
    this.renderer.setStyle(this.el.nativeElement, 'max-width', `${newWidth}px`);
  }

  private onMouseUp(event: MouseEvent): void {
    if (!this.isResizing) return;

    event.preventDefault();
    event.stopPropagation();

    this.isResizing = false;
    this.renderer.setStyle(this.resizeHandle, 'background-color', 'transparent');
    this.renderer.removeClass(document.body, 'resizing-column');
    this.renderer.setStyle(document.body, 'cursor', 'default');
    this.renderer.setStyle(document.body, 'user-select', 'auto');

    if (this.hasDragged) {
      this.saveWidth(this.el.nativeElement.offsetWidth);
      // Suppress the click event that fires after mouseup so mat-sort-header is not triggered
      const th = this.el.nativeElement as HTMLElement;
      const preventClick = (e: MouseEvent) => {
        e.stopPropagation();
        th.removeEventListener('click', preventClick, true);
      };
      th.addEventListener('click', preventClick, true);
    }

    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }
}
